import React, { useState, useEffect, useRef } from 'react';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  EllipsisVerticalIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';
import {
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  UserCircleIcon as UserCircleIconSolid
} from '@heroicons/react/24/solid';
import chatService from '../services/chatService';

const ChatSystem = ({ currentUser, isOpen, onToggle }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  // Initialize chat service
  useEffect(() => {
    if (currentUser) {
      const userType = currentUser.role === 'admin' || currentUser.userType === 'admin' ? 'admin' : 'employee';
      chatService.initialize(currentUser.id || currentUser._id, userType);
      loadConversations();
      loadOnlineUsers();

      // Add chat event listeners
      const handleChatEvent = (eventType, data) => {
        switch (eventType) {
          case 'messageSent':
            handleMessageSent(data);
            break;
          case 'messagesRead':
            handleMessagesRead(data);
            break;
          case 'messageReceived':
            // Reload messages for the conversation
            if (selectedConversation && data.conversationId === selectedConversation._id) {
              loadMessages(selectedConversation._id);
            }
            loadConversations();
            break;
          case 'conversationAdded':
            // Reload conversations
            loadConversations();
            break;
          default:
            break;
        }
      };

      chatService.addListener(handleChatEvent);

      return () => {
        chatService.removeListener(handleChatEvent);
        chatService.cleanup();
      };
    }
  }, [currentUser]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update unread count
  useEffect(() => {
    const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
    setUnreadCount(totalUnread);
  }, [conversations]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    setLoading(true);
    try {
      const result = await chatService.getConversations();
      if (result.success) {
        setConversations(result.conversations);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    setLoading(true);
    try {
      const result = await chatService.getMessages(conversationId);
      if (result.success) {
        setMessages(result.messages);
        // Mark messages as read
        chatService.markAsRead(conversationId);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOnlineUsers = async () => {
    try {
      const result = await chatService.getOnlineUsers();
      if (result.success) {
        setOnlineUsers(result.users);
      }
    } catch (error) {
      console.error('Error loading online users:', error);
    }
  };

  const handleNewMessages = (newMessages) => {
    if (selectedConversation) {
      const relevantMessages = newMessages.filter(msg => msg.conversationId === selectedConversation._id);
      if (relevantMessages.length > 0) {
        setMessages(prev => [...prev, ...relevantMessages]);
      }
    }
    
    // Update conversations with new message info
    loadConversations();
  };

  const handleMessageSent = (message) => {
    if (selectedConversation && message.conversationId === selectedConversation._id) {
      setMessages(prev => [...prev, message]);
    }
    loadConversations();
  };

  const handleMessagesRead = (data) => {
    setConversations(prev => 
      prev.map(conv => 
        conv._id === data.conversationId 
          ? { ...conv, unreadCount: 0 }
          : conv
      )
    );
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const messageContent = newMessage.trim();
    setNewMessage('');

    try {
      const result = await chatService.sendMessage(selectedConversation._id, messageContent);
      
      if (result.success) {
        // Reload messages to show the sent message
        setTimeout(() => {
          loadMessages(selectedConversation._id);
        }, 100);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore message if failed
      setNewMessage(messageContent);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation._id);
    
    // Mark as read
    setConversations(prev => 
      prev.map(conv => 
        conv._id === conversation._id 
          ? { ...conv, unreadCount: 0 }
          : conv
      )
    );
    
    // Welcome message disabled to avoid interference with real messages
  };

  const createNewConversation = async (user) => {
    try {
      const result = await chatService.createConversation(
        user.userId, 
        user.userType, 
        `Chat with ${user.name}`
      );
      
      if (result.success) {
        setConversations(prev => [result.conversation, ...prev]);
        setSelectedConversation(result.conversation);
        setShowNewChatModal(false);
        loadMessages(result.conversation._id);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'away': return 'bg-yellow-500';
      case 'busy': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const filteredUsers = onlineUsers.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    user.userId !== (currentUser.id || currentUser._id)
  );

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={onToggle}
          className="relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
        >
          <ChatBubbleLeftRightIconSolid className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 w-96 h-[600px] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ChatBubbleLeftRightIconSolid className="h-6 w-6" />
            <div>
              <h3 className="font-semibold">
                {selectedConversation ? selectedConversation.title : 'Messages'}
              </h3>
              {selectedConversation && (
                <p className="text-sm text-blue-100">
                  {selectedConversation.participants.length} participants
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {selectedConversation && (
              <>
                <button className="p-1 hover:bg-white/20 rounded">
                  <PhoneIcon className="h-5 w-5" />
                </button>
                <button className="p-1 hover:bg-white/20 rounded">
                  <VideoCameraIcon className="h-5 w-5" />
                </button>
              </>
            )}
            <button
              onClick={onToggle}
              className="p-1 hover:bg-white/20 rounded"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {!selectedConversation ? (
          /* Conversation List */
          <div className="flex-1 flex flex-col">
            {/* Search and New Chat */}
            <div className="p-3 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => setShowNewChatModal(true)}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                  <ChatBubbleLeftRightIcon className="h-12 w-12 mb-2" />
                  <p>No conversations yet</p>
                  <button
                    onClick={() => setShowNewChatModal(true)}
                    className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Start a new chat
                  </button>
                </div>
              ) : (
                conversations
                  .filter(conv => 
                    conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    conv.participants.some(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
                  )
                  .map((conversation) => (
                    <div
                      key={conversation._id}
                      onClick={() => selectConversation(conversation)}
                      className="p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <UserCircleIconSolid className="h-12 w-12 text-gray-400" />
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900 truncate">
                              {conversation.title}
                            </h4>
                            <span className="text-xs text-gray-500">
                              {conversation.lastMessage && formatTime(conversation.lastMessage.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 truncate">
                            {conversation.lastMessage?.content || 'No messages yet'}
                          </p>
                        </div>
                        {conversation.unreadCount > 0 && (
                          <span className="bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        ) : (
          /* Chat Messages */
          <div className="flex-1 flex flex-col">
            {/* Back Button */}
            <div className="p-3 border-b border-gray-200">
              <button
                onClick={() => setSelectedConversation(null)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                ← Back to conversations
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((message) => {
                  const isOwn = message.senderId === (currentUser.id || currentUser._id);
                  return (
                    <div
                      key={message._id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        isOwn 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-900'
                      }`}>
                        {!isOwn && (
                          <p className="text-xs font-medium mb-1 opacity-70">
                            {message.senderName}
                          </p>
                        )}
                        <p className="text-sm">{message.content}</p>
                        <p className={`text-xs mt-1 ${
                          isOwn ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <input
                  ref={messageInputRef}
                  type="text"
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Chat Modal */}
        {showNewChatModal && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="bg-white rounded-lg p-6 w-96 max-h-[500px] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Start New Chat</h3>
                <button
                  onClick={() => setShowNewChatModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search employees..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <UserCircleIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No employees found</p>
                    <p className="text-sm text-gray-400">Try adjusting your search</p>
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.userId}
                      onClick={() => createNewConversation(user)}
                      className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-gray-200"
                    >
                      <div className="relative">
                        <UserCircleIconSolid className="h-12 w-12 text-blue-400" />
                        <div className={`absolute bottom-0 right-0 w-3 h-3 ${getStatusColor(user.status)} rounded-full border-2 border-white`}></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{user.name}</p>
                        <p className="text-sm text-gray-500 truncate">{user.role || user.userType}</p>
                        {user.email && (
                          <p className="text-xs text-gray-400 truncate">{user.email}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          user.status === 'online' ? 'bg-green-100 text-green-800' :
                          user.status === 'away' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {filteredUsers.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center">
                    {filteredUsers.length} employee{filteredUsers.length !== 1 ? 's' : ''} available
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSystem;
